import React from "react";

import { makeStyles } from "@material-ui/core/styles";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import { Checkbox, IconButton, ListSubheader, Tooltip } from "@material-ui/core";
import FilterListIcon from "@material-ui/icons/FilterList";
import SelectAllIcon from "@material-ui/icons/SelectAll";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
	selectAllItem: {
		borderBottom: `1px solid ${theme.palette.divider}`,
		cursor: "pointer",
		lineHeight: "normal",
		marginBottom: 4,
		paddingBottom: 4,
		paddingTop: 4,
		"&:hover": {
			backgroundColor: theme.palette.action.hover,
		},
	},
	menuItem: {
		"& $onlyButton": {
			opacity: 0,
			pointerEvents: "none",
		},
		"&:hover $onlyButton, &:focus-within $onlyButton": {
			opacity: 1,
			pointerEvents: "auto",
		},
		"@media (hover: none), (pointer: coarse)": {
			"& $onlyButton": {
				opacity: 1,
				pointerEvents: "auto",
			},
		},
	},
	menuItemContent: {
		display: "flex",
		alignItems: "center",
		width: "100%",
		minWidth: 0,
	},
	userInfo: {
		alignItems: "center",
		display: "inline-flex",
		flex: 1,
		gap: 8,
		justifyContent: "space-between",
		minWidth: 0,
	},
	userName: {
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	selectAllIcon: {
		color: theme.palette.text.secondary,
		marginLeft: 8,
		marginRight: 11,
	},
	presenceDot: {
		borderRadius: "50%",
		display: "inline-block",
		flexShrink: 0,
		height: 16,
		width: 16,
	},
	onlyButton: {
		borderRadius: 4,
		boxShadow: "none",
		color: theme.palette.text.secondary,
		backgroundColor: theme.palette.action.hover,
		height: 32,
		marginLeft: theme.spacing(1),
		padding: 4,
		transition: theme.transitions.create(["background-color", "opacity"], {
			duration: theme.transitions.duration.shortest,
		}),
		width: 32,
		"&:hover": {
			boxShadow: "none",
			backgroundColor: theme.palette.action.selected,
		},
	},
}));

const TicketsUserSelect = ({
	users = [],
	selectedUserIds = [],
	includeUnassigned = false,
	onChange,
	onSelectOnly,
	onSelectUnassignedOnly,
	onToggleUnassigned,
	onSelectAll,
	onClearAll,
}) => {
	const classes = useStyles();
	const userIds = users
		.map(loadedUser => Number(loadedUser.id))
		.filter(userId => Number.isFinite(userId));
	const selectedUserIdSet = new Set(
		(selectedUserIds || []).map(selectedUserId => Number(selectedUserId))
	);
	const allSelected =
		userIds.length > 0 &&
		userIds.every(userId => selectedUserIdSet.has(userId)) &&
		includeUnassigned;
	const toggleAllTooltip = allSelected
		? i18n.t("ticketsUserSelect.clearAllTooltip")
		: i18n.t("ticketsUserSelect.selectAllTooltip");
	const selectedValues = includeUnassigned
		? [...selectedUserIds, "__unassigned"]
		: selectedUserIds;

	const handleChange = event => {
		const selectedValues = event.target.value || [];

		if (selectedValues.includes("__all")) {
			if (allSelected && onClearAll) {
				onClearAll();
			} else if (onSelectAll) {
				onSelectAll();
			}
			return;
		}

		const nextIncludeUnassigned = selectedValues.includes("__unassigned");
		if (nextIncludeUnassigned !== includeUnassigned && onToggleUnassigned) {
			onToggleUnassigned();
		}

		onChange(
			selectedValues
				.map(value => Number(value))
				.filter(value => Number.isFinite(value))
		);
	};

	const handleSelectUnassignedOnly = event => {
		event.preventDefault();
		event.stopPropagation();
		if (onSelectUnassignedOnly) {
			onSelectUnassignedOnly();
		}
	};

	const handleSelectOnly = (event, userId) => {
		event.preventDefault();
		event.stopPropagation();
		if (onSelectOnly) {
			onSelectOnly(userId);
		}
	};

	const handleSelectAll = event => {
		event.preventDefault();
		event.stopPropagation();
		if (allSelected && onClearAll) {
			onClearAll();
		} else if (onSelectAll) {
			onSelectAll();
		}
	};

	return (
		<div style={{ width: "100%", minWidth: 0, marginTop: -4 }}>
			<FormControl fullWidth margin="dense">
				<Select
					multiple
					displayEmpty
					variant="outlined"
					value={selectedValues}
					onChange={handleChange}
					MenuProps={{
						anchorOrigin: {
							vertical: "bottom",
							horizontal: "left",
						},
						transformOrigin: {
							vertical: "top",
							horizontal: "left",
						},
						getContentAnchorEl: null,
					}}
					renderValue={() => i18n.t("ticketsUserSelect.placeholder")}
				>
					<ListSubheader
						className={classes.selectAllItem}
						disableSticky
						onMouseDown={event => {
							event.preventDefault();
							event.stopPropagation();
						}}
						onClick={handleSelectAll}
					>
						<div className={classes.menuItemContent}>
							<SelectAllIcon className={classes.selectAllIcon} fontSize="small" />
							<div className={classes.userInfo}>
								<span className={classes.userName}>
									{i18n.t("ticketsUserSelect.all")}
								</span>
							</div>
							<Tooltip title={toggleAllTooltip}>
								<IconButton
									aria-label={toggleAllTooltip}
									className={classes.onlyButton}
									onMouseDown={event => {
										event.preventDefault();
										event.stopPropagation();
									}}
									onClick={handleSelectAll}
									size="small"
								>
									<FilterListIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						</div>
					</ListSubheader>
					<MenuItem className={classes.menuItem} dense value="__unassigned">
						<div className={classes.menuItemContent}>
							<Checkbox
								size="small"
								color="primary"
								checked={includeUnassigned}
							/>
							<div className={classes.userInfo}>
								<span className={classes.userName}>
									{i18n.t("ticketsUserSelect.unassigned")}
								</span>
							</div>
							<Tooltip
								title={
									includeUnassigned && selectedUserIds.length === 0
										? i18n.t("ticketsUserSelect.allTooltip")
										: i18n.t("ticketsUserSelect.onlyTooltip")
								}
							>
								<IconButton
									aria-label={
										includeUnassigned && selectedUserIds.length === 0
											? i18n.t("ticketsUserSelect.allTooltip")
											: i18n.t("ticketsUserSelect.onlyTooltip")
									}
									className={classes.onlyButton}
									onMouseDown={event => {
										event.preventDefault();
										event.stopPropagation();
									}}
									onClick={handleSelectUnassignedOnly}
									size="small"
								>
									<FilterListIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						</div>
					</MenuItem>
					{users.map(loadedUser => (
						<MenuItem
							className={classes.menuItem}
							dense
							key={loadedUser.id}
							value={Number(loadedUser.id)}
						>
							<div className={classes.menuItemContent}>
								<Checkbox
									size="small"
									color="primary"
									checked={selectedUserIds.indexOf(Number(loadedUser.id)) > -1}
								/>
								<div className={classes.userInfo}>
									<span className={classes.userName}>{loadedUser.name}</span>
									<span
										className={classes.presenceDot}
										style={{
											backgroundColor: loadedUser?.isOnline ? "#2e7d32" : "#d32f2f",
										}}
									/>
								</div>
								<Tooltip
									title={
										selectedUserIds.length === 1 &&
										selectedUserIds[0] === Number(loadedUser.id)
											? i18n.t("ticketsUserSelect.allTooltip")
											: i18n.t("ticketsUserSelect.onlyTooltip")
									}
								>
									<IconButton
										aria-label={
											selectedUserIds.length === 1 &&
											selectedUserIds[0] === Number(loadedUser.id)
												? i18n.t("ticketsUserSelect.allTooltip")
												: i18n.t("ticketsUserSelect.onlyTooltip")
										}
										className={classes.onlyButton}
										onMouseDown={event => {
											event.preventDefault();
											event.stopPropagation();
										}}
										onClick={event => handleSelectOnly(event, loadedUser.id)}
										size="small"
									>
										<FilterListIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							</div>
						</MenuItem>
					))}
				</Select>
			</FormControl>
		</div>
	);
};

export default TicketsUserSelect;
